import React from 'react';

/**
 * A small trend chart with the reference range shaded behind it.
 *
 * Hand-drawn SVG rather than a Recharts instance: these render 26 at a time on
 * the trends grid, and a full chart library per card is both slow and visually
 * heavier than this needs to be.
 */
export default function Sparkline({
  points,
  low,
  high,
  color = 'var(--accent)',
  width = 260,
  height = 64,
}) {
  // Called before any early return — hooks must run in the same order on every
  // render, and there is a `return null` below for series too short to draw.
  const gradientId = `spark-${React.useId()}`;

  const values = points.map((p) => p.value);
  if (values.length < 2) return null;

  const pad = 6;
  const innerW = width - pad * 2;
  const innerH = height - pad * 2;

  // Scale to the data and the reference band together, so the band is always
  // visible for context even when every reading sits well inside it. `high` is
  // sometimes a sentinel (eGFR's 999, HDL's 250) — clamping to the data keeps
  // one absurd bound from flattening the series into a straight line.
  const dataMin = Math.min(...values);
  const dataMax = Math.max(...values);
  const span = dataMax - dataMin || Math.abs(dataMax) * 0.1 || 1;

  const bandLow = Math.max(low, dataMin - span * 2);
  const bandHigh = Math.min(high, dataMax + span * 2);

  const minY = Math.min(dataMin, bandLow) - span * 0.25;
  const maxY = Math.max(dataMax, bandHigh) + span * 0.25;
  const range = maxY - minY || 1;

  const x = (i) => pad + (i / (values.length - 1)) * innerW;
  const y = (v) => pad + innerH - ((v - minY) / range) * innerH;

  const line = values.map((v, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(v)}`).join(' ');
  const area = `${line} L ${x(values.length - 1)} ${pad + innerH} L ${x(0)} ${pad + innerH} Z`;

  const bandTop = y(Math.min(bandHigh, maxY));
  const bandBottom = y(Math.max(bandLow, minY));

  return (
    <svg
      width="100%"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={`Trend from ${values[0]} to ${values[values.length - 1]}`}
      style={{ display: 'block' }}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.22" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Reference range */}
      <rect
        x={0}
        y={bandTop}
        width={width}
        height={Math.max(1, bandBottom - bandTop)}
        fill="var(--ok)"
        opacity="0.09"
      />
      <line x1={0} x2={width} y1={bandTop} y2={bandTop}
        stroke="var(--ok)" strokeOpacity="0.3" strokeWidth="1" strokeDasharray="3 3" />
      <line x1={0} x2={width} y1={bandBottom} y2={bandBottom}
        stroke="var(--ok)" strokeOpacity="0.3" strokeWidth="1" strokeDasharray="3 3" />

      <path d={area} fill={`url(#${gradientId})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" />

      {/* Endpoint emphasised — it is the value the reader actually cares about */}
      <circle cx={x(values.length - 1)} cy={y(values[values.length - 1])} r="3.5"
        fill={color} stroke="var(--surface)" strokeWidth="1.5" />
      <circle cx={x(0)} cy={y(values[0])} r="2.5"
        fill="var(--text-faint)" />
    </svg>
  );
}
